import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { createTenant, listTenants, blockTenantAccess, unblockTenantAccess, changeTenantPlan } from "../../../../services/platform_admin_service";

/**
 * usePlatformTenants — Hook paginado para listagem de tenants.
 */
const usePlatformTenants = () => {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalItems: 0, totalPages: 0 });
    const [filters, setFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTenants = useCallback(async (page = 1, pageSize = 25, currentFilters = {}) => {
        try {
            setLoading(true);
            setError(null);
            const result = await listTenants({ page, pageSize, filters: currentFilters });
            setItems(result.items || []);
            setPagination(result.pagination || {});
        } catch (err) {
            console.error("[usePlatformTenants]", err);
            setError(err.message || "Erro ao carregar academias.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTenants(1, 25, filters);
    }, [fetchTenants, filters]);

    const goToPage = (page) => {
        fetchTenants(page, pagination.pageSize, filters);
    };

    const applyFilters = (newFilters) => {
        setFilters(newFilters);
    };

    const createNewTenant = async (payload) => {
        try {
            setLoading(true);
            setError(null);
            
            const formattedPayload = {
                tenant: {
                    name: payload.name,
                    corporateName: payload.corporateName || "",
                    document: payload.document || "",
                    email: payload.email,
                    phone: payload.phone || "",
                    address: {
                        zipCode: payload.zipCode || "",
                        street: payload.street || "",
                        number: payload.number || "",
                        complement: payload.complement || "",
                        neighborhood: payload.neighborhood || "",
                        city: payload.city || "",
                        state: payload.state || "",
                    }
                },
                owner: {
                    fullName: payload.responsibleName,
                    name: payload.responsibleName,
                    email: payload.email,
                    password: payload.password || "123456",
                    phone: payload.phone || "",
                    cpf: "",
                },
                billing: {
                    platformPlanId: payload.planId,
                    billingStatus: payload.status,
                    trialStartDate: payload.status === "trial" ? (payload.trialStartDate || null) : null,
                    trialEndsAt: payload.status === "trial" ? (payload.trialEndsAt || null) : null,
                    active: true
                }
            };

            const result = await createTenant(formattedPayload);
            await fetchTenants(1, pagination.pageSize || 25, filters);
            toast.success("Academia cadastrada com sucesso!");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao cadastrar academia.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const blockTenant = async (idTenant, reason) => {
        try {
            setLoading(true);
            setError(null);
            const result = await blockTenantAccess(idTenant, reason);
            await fetchTenants(pagination.page || 1, pagination.pageSize || 25, filters);
            toast.success("Acesso da academia bloqueado.");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao bloquear tenant.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const unblockTenant = async (idTenant) => {
        try {
            setLoading(true);
            setError(null);
            const result = await unblockTenantAccess(idTenant);
            await fetchTenants(pagination.page || 1, pagination.pageSize || 25, filters);
            toast.success("Acesso da academia restaurado.");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao desbloquear tenant.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const changePlan = async (idTenant, newPlanCode) => {
        try {
            setLoading(true);
            setError(null);
            const result = await changeTenantPlan(idTenant, newPlanCode);
            await fetchTenants(pagination.page || 1, pagination.pageSize || 25, filters);
            toast.success("Plano alterado com sucesso!");
            return result;
        } catch (err) {
            const msg = err.message || "Erro ao alterar plano.";
            setError(msg);
            toast.error(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        items,
        pagination,
        loading,
        error,
        goToPage,
        applyFilters,
        filters,
        createNewTenant,
        blockTenant,
        unblockTenant,
        changePlan,
        refetch: () => fetchTenants(pagination.page, pagination.pageSize, filters),
    };
};

export default usePlatformTenants;
